/* eslint-disable no-param-reassign */
import { FunctionComponent, SVGProps } from 'react';
import Highcharts, { AxisPlotBandsOptions, SeriesOptions } from 'highcharts';
import type { PanelType } from '@webapp/bff';
import { errorReport } from './errors';
import ClockIcon from '../svg/clock.svg?react';
import TrafficIcon from '../svg/traffic.svg?react';
import SpeedometerIcon from '../svg/speedometer.svg?react';
import imagePathControlKnob from '../svg/control-knob.svg?url';
import imagePathControlPlus from '../svg/control-plus.svg?url';

import {
  getAnomalyScoreColor,
  getAnomalyScorePercentage,
} from '../data/useOrgRoutes';
import { SiteInfo } from '../data/useSiteData';

export type LegacyInsightType =
  | 'speed'
  | 'journeyTime'
  | 'timeDelay'
  | 'queueLength'
  | 'siteImpactDelay'
  | 'dust'
  | 'noise'
  | 'rain'
  | 'count'
  | 'heavyCount';

export type InsightType =
  | 'TIME_DELAY'
  | 'ENVIRONMENT'
  | 'DUST'
  | 'JOURNEY_TIME'
  | 'NOISE'
  | 'QUEUE_LENGTH'
  | 'RAIN'
  | 'SITE_IMPACT_DELAY'
  | 'SPEED'
  | 'VEHICLE_STATION_COUNT'
  | 'BUS_JOURNEY_TIME'
  | 'BUS_DELAY'
  | 'BUS_SITE_IMPACT'
  | 'VEHICLE_COUNT'
  | 'BUS_ACQUIRED_DELAY'
  | 'COMPLETED_TRIP_TIME'
  | 'DWELL_TIME'
  | LegacyInsightType;
export interface InsightPerformanceConfig extends InsightConfig {
  formatScoreValue?: (value: number) => string;
  colorForScoreValue?: (value: number) => string;
}

export interface InsightConfig {
  iconSVG?: FunctionComponent<
    SVGProps<SVGSVGElement> & { title?: string | undefined }
  >;
  title: string;
  formatValue: (value: number) => string;
  kpi?: {
    label: string;
    value: number;
  };
}

export type ChartType = 'line' | 'column' | 'scatter';

interface InsightChartAxisConfig {
  type: ChartType;
  title?: string;
  formatValue?: (value: number) => string;
  tooltipFormatValue?: (value: number) => string;
  interval?: number;
  plotBands?: AxisPlotBandsOptions[];
  softMin?: number;
}
export interface InsightChartConfig {
  title: string;
  mainAxis: InsightChartAxisConfig;
  secondaryAxis?: InsightChartAxisConfig;
  kpi?: {
    label: string;
    value: number;
  };
  zoomingType?: 'x' | 'y' | 'xy';
  marker?: {
    enabled: boolean;
    symbol: 'circle' | 'square' | 'triangle' | 'diamond' | 'triangle-down';
    radius: number;
  };
  disableCrosshair?: boolean;
  disableTooltip?: boolean;
  legend?: {
    enabled?: boolean;
    reversed?: boolean;
  };
}

export interface InsightChartSeries extends SeriesOptions {
  data: [number, number?, number?][];
  color: string;
  keys: string[];
  showInLegend?: boolean;
  visible?: boolean;
  custom?: Record<string, any>;
  animation?: boolean;
  dashStyle?: string;
}

export function getTitleFromPanelType(
  panelType: PanelType,
  formatMessage: (message: any) => string
): {
  title: string;
  info?: {
    label: string;
    description: string;
  };
} {
  switch (panelType) {
    case 'TIME_DELAY':
      return {
        title: formatMessage({
          defaultMessage: 'Time delay',
          id: '0eGHl8',
        }),
        info: {
          label: formatMessage({
            defaultMessage: 'What is time delay?',
            id: '66odbK',
          }),
          description: formatMessage({
            defaultMessage:
              'The additional time that it takes a vehicle to complete the entirety of a route compared to free-flow conditions.',
            id: 'Gt9Qlf',
          }),
        },
      };
    case 'DUST':
      return {
        title: formatMessage({
          defaultMessage: 'Environment',
          id: 'K7kuIZ',
        }),
      };
    case 'JOURNEY_TIME':
      return {
        title: formatMessage({
          defaultMessage: 'Journey time',
          id: 'ULp9cT',
        }),
        info: {
          label: formatMessage({
            defaultMessage: 'What is journey time?',
            id: 'knejUe',
          }),
          description: formatMessage({
            defaultMessage:
              'The time taken to complete the entirety of the route. The time captured represents the current journey time if you were to begin driving the route. If multiple records are captured within a 15-minute timebox, the maximum value is displayed.',
            id: '37jK4V',
          }),
        },
      };
    case 'NOISE':
      return {
        title: formatMessage({
          defaultMessage: 'Noise',
          id: 'WiQKGw',
        }),
      };
    case 'QUEUE_LENGTH':
      return {
        title: formatMessage({
          defaultMessage: 'Queue length',
          id: 'eeGw5K',
        }),
        info: {
          label: formatMessage({
            defaultMessage: 'What is queue length?',
            id: 'MnzuFf',
          }),
          description: formatMessage({
            defaultMessage: `Calculated by subsegments of a route.

Routes are broken into micro-segments, with the performance of each micro-segment measured for the presence of queuing. If queueing is present within a micro-segment within a route, it is added to the total queue length.

Queueing within micro-segments must be sequential from the end of a route for it to be regarded as queueing. The end of the route is believed to be the source of any queues.

Queueing is considered to be present when the journey time is more than double the free-flow value.`,
            id: 'A9P0KD',
          }),
        },
      };
    case 'RAIN':
      return {
        title: formatMessage({
          defaultMessage: 'Rain',
          id: 'xtRgyp',
        }),
      };

    case 'SITE_IMPACT_DELAY':
      return {
        title: formatMessage({
          defaultMessage: 'Site impact',
          id: '3UQPPp',
        }),
        info: {
          label: formatMessage({
            defaultMessage: 'What is site impact?',
            id: 'FrqnJn',
          }),
          description: formatMessage({
            defaultMessage:
              'Additional delay compared to a baseline or comparison period. The calculation is dynamically driven against the selected baseline comparison.',
            id: 'ShxdbG',
          }),
        },
      };
    case 'SPEED':
      return {
        title: formatMessage({
          defaultMessage: 'Average speed',
          id: 'nf49ym',
        }),
        info: {
          label: formatMessage({
            defaultMessage: 'What is average speed?',
            id: 'ED26jZ',
          }),
          description: formatMessage({
            defaultMessage:
              'The journey time divided by the distance of the route.',
            id: 'J4xMOJ',
          }),
        },
      };
    case 'VEHICLE_STATION_COUNT':
      return {
        title: formatMessage({
          defaultMessage: 'Vehicle count',
          id: 'gQNidF',
        }),
        info: {
          label: formatMessage({
            defaultMessage: 'What is vehicle count?',
            id: '2PPWUA',
          }),
          description: formatMessage({
            defaultMessage:
              'The number of vehicles recorded to have driven through selected count locations.',
            id: 'Efor1y',
          }),
        },
      };
    case 'BUS_JOURNEY_TIME':
      return {
        title: formatMessage({
          defaultMessage: 'Bus journey time',
          id: '3+lf2a',
        }),
        info: {
          label: formatMessage({
            defaultMessage: 'What is bus journey time?',
            id: '5OLMo7',
          }),
          description: formatMessage({
            defaultMessage:
              'The time taken for a bus to complete the defined segment. Journey times are recorded per bus upon completion of the trip.',
            id: 'uWeG8/',
          }),
        },
      };
    case 'BUS_SITE_IMPACT':
      return {
        title: formatMessage({
          defaultMessage: 'Bus site impact',
          id: 'LRkl0P',
        }),
        info: {
          label: formatMessage({
            defaultMessage: 'What is bus site impact?',
            id: '3X63Pt',
          }),
          description: formatMessage({
            defaultMessage:
              'Additional delay compared to a baseline or comparison period. The calculation is dynamically driven against the selected baseline comparison.',
            id: 'ShxdbG',
          }),
        },
      };
    case 'BUS_ACQUIRED_DELAY':
      return {
        title: formatMessage({
          defaultMessage: 'Bus accrued delay',
          id: 'wk9Blh',
        }),
        info: {
          label: formatMessage({
            defaultMessage: 'What is maximum bus accrued delay?',
            id: 'T0zG4x',
          }),
          description: formatMessage({
            defaultMessage:
              'Accrued delay calculates the additional delay versus the schedule that has been added within the defined bus segment. Any delay that is present before a bus enters a segment is removed. The maximum accrued delay is displayed in a 15-minute time block.',
            id: '3V++wE',
          }),
        },
      };
    case 'BUS_DELAY':
      return {
        title: formatMessage({
          defaultMessage: 'Maximum delay versus schedule',
          id: '2U6SlO',
        }),
        info: {
          label: formatMessage({
            defaultMessage: 'What is maximum delay versus schedule?',
            id: 'g3QEcq',
          }),
          description: formatMessage({
            defaultMessage:
              'While buses are travelling within the defined segment, we continuously record any delays versus the schedule. The maximum delay is then shown in 15-minute time blocks. Monitoring delay versus schedule throughout the journey allows Mooven to provide proactive alerts as soon as bus delays exceed defined KPIs, even between stops.',
            id: 'u+lYVU',
          }),
        },
      };
    case 'COMPLETED_TRIP_TIME':
      return {
        title: formatMessage({
          defaultMessage: 'Completed trip time',
          id: 'on/fHc',
        }),
      };
    case 'DWELL_TIME':
      return {
        title: formatMessage({
          defaultMessage: 'Dwell time',
          id: '/wBZRE',
        }),
      };
    case 'VEHICLE_COUNT_HEATMAP':
      return {
        title: formatMessage({
          defaultMessage: 'Vehicle count profile',
          id: 'LVkVWr',
        }),
        info: {
          label: formatMessage({
            defaultMessage: 'What is vehicle count profile?',
            id: 'EKshv4',
          }),
          description: formatMessage({
            defaultMessage:
              'This graph represents the average peak volume per hour. Volume data is collected every 15min and averaged across an hour to give an hourly representation of traffic volume.',
            id: 'YkSVfp',
          }),
        },
      };
    case 'INCIDENTS':
      return {
        title: formatMessage({
          defaultMessage: 'Conditions and events',
          id: 'VzFTEf',
        }),
        info: {
          label: formatMessage({
            defaultMessage: 'What are conditions and events?',
            id: 'pPxzL/',
          }),
          description: formatMessage({
            defaultMessage:
              'Conditions and events in Mooven highlight the location of impactful events near your site, helping you to understand factors affecting traffic behaviour. Data from TomTom and HERE services is used to populate conditions or events.',
            id: 'H/+wGV',
          }),
        },
      };
    case 'SUMMARY':
      return {
        title: formatMessage({
          defaultMessage: 'Summary and export',
          id: 'VqhEWH',
        }),
        info: {
          label: formatMessage({
            defaultMessage: 'What is summary and export?',
            id: 'faoY1A',
          }),
          description: formatMessage({
            defaultMessage:
              'The summary panel gives a quick view of the worst-performing routes over the past 24 hours. The “Export all as CSV” panel allows you to export all floating vehicle data in one CSV.',
            id: 'Ia4rtz',
          }),
        },
      };
    case 'WEATHER':
      return {
        title: formatMessage({
          defaultMessage: 'Weather at site location',
          id: 'Smh/qU',
        }),
        info: {
          label: formatMessage({
            defaultMessage: 'What is this weather data?',
            id: 'Dv5pDm',
          }),
          description: formatMessage({
            defaultMessage:
              'Hourly weather data is sourced from Open-Meteo, which blends station observations, satellite inputs and other sources to provide accurate local estimates, even without stations close to your site. Recent data (the last 5 days) is from forecasts rather than observations. Data shown is for your Site Location which can be updated in the Edit Site menu.',
            id: 'COZqVy',
          }),
        },
      };
    default:
      return {
        title: panelType,
      };
  }
}
export function getInsightConfigFromType(
  insightType: InsightType,
  siteData: Partial<
    Pick<SiteInfo, 'siteUnit' | 'delayKPI' | 'queueKPI' | 'speedKPI'>
  >,
  formatMessage
): InsightConfig {
  switch (insightType) {
    case 'TIME_DELAY':
    case 'timeDelay':
      return {
        ...getTitleFromPanelType('TIME_DELAY', formatMessage),
        formatValue: formatSeconds,
        kpi: !siteData.delayKPI
          ? undefined
          : {
              label: formatMessage({
                defaultMessage: 'Delay KPI',
                id: 'bk5ncy',
                description: 'Time delay chart kpi label',
              }),
              value: siteData.delayKPI,
            },
      };
    case 'DUST':
    case 'dust':
      return {
        ...getTitleFromPanelType('DUST', formatMessage),
        formatValue: (value) => `${Math.round(value)}ug/m3`,
        kpi: !siteData.delayKPI
          ? undefined
          : {
              label: formatMessage({
                defaultMessage: 'Delay KPI',
                id: 'bk5ncy',
                description: 'Time delay chart kpi label',
              }),
              value: siteData.delayKPI,
            },
      };
    case 'JOURNEY_TIME':
    case 'journeyTime':
      return {
        iconSVG: ClockIcon,
        ...getTitleFromPanelType('JOURNEY_TIME', formatMessage),
        formatValue: (value) => formatSeconds(value),
      };
    case 'NOISE':
    case 'noise':
      return {
        ...getTitleFromPanelType('NOISE', formatMessage),
        formatValue: (value) => `${Math.round(value)}dB`,
      };
    case 'QUEUE_LENGTH':
    case 'queueLength':
      return {
        iconSVG: TrafficIcon,
        ...getTitleFromPanelType('QUEUE_LENGTH', formatMessage),
        formatValue: (value) =>
          `${
            siteData.siteUnit === 'IMPERIAL'
              ? (value * 0.000621371).toFixed(2)
              : value.toFixed(0)
          }${siteData.siteUnit === 'IMPERIAL' ? 'mi' : 'm'}`,
        kpi: !siteData.queueKPI
          ? undefined
          : {
              label: formatMessage({
                defaultMessage: 'Queue KPI',
                id: 'quOsC1',
                description: 'Queue chart kpi label',
              }),
              value: siteData.queueKPI,
            },
      };
    case 'RAIN':
    case 'rain':
      return {
        ...getTitleFromPanelType('RAIN', formatMessage),
        formatValue: (value: number) => `${value}mm`,
      };
    case 'BUS_SITE_IMPACT':
      return {
        ...getTitleFromPanelType('BUS_SITE_IMPACT', formatMessage),
        formatValue: (value) => formatSeconds(value),
        kpi: undefined,
      };
    case 'SITE_IMPACT_DELAY':
    case 'siteImpactDelay':
      return {
        ...getTitleFromPanelType('SITE_IMPACT_DELAY', formatMessage),
        formatValue: (value) => formatSeconds(value),
        kpi: !siteData.delayKPI
          ? undefined
          : {
              label: formatMessage({
                defaultMessage: 'Delay KPI',
                id: 'QjOdRj',
                description: 'Site impact delay chart kpi label',
              }),
              value: siteData.delayKPI,
            },
      };
    case 'SPEED':
    case 'speed':
      return {
        iconSVG: SpeedometerIcon,
        ...getTitleFromPanelType('SPEED', formatMessage),
        formatValue: (value: number) =>
          `${Math.round(
            siteData.siteUnit === 'IMPERIAL' ? 0.621371 * value : value
          )}${siteData.siteUnit === 'IMPERIAL' ? 'mph' : 'kmph'}`,
        kpi: !siteData.speedKPI
          ? undefined
          : {
              label: formatMessage({
                defaultMessage: 'Speed KPI',
                id: '7krvwe',
                description: 'Speed chart kpi label',
              }),
              value: siteData.speedKPI,
            },
      };
    case 'VEHICLE_STATION_COUNT':
    case 'count':
      return {
        ...getTitleFromPanelType('VEHICLE_STATION_COUNT', formatMessage),
        formatValue: (value) => `${Math.round(value)}`,
      };
    case 'BUS_JOURNEY_TIME':
      return {
        ...getTitleFromPanelType('BUS_JOURNEY_TIME', formatMessage),
        formatValue: (value) => formatSeconds(value),
      };
    case 'BUS_ACQUIRED_DELAY':
      return {
        ...getTitleFromPanelType('BUS_ACQUIRED_DELAY', formatMessage),
        formatValue: (value) => formatSeconds(value),
      };
    case 'BUS_DELAY':
      return {
        ...getTitleFromPanelType('BUS_DELAY', formatMessage),
        formatValue: (value) => formatSeconds(value),
      };
    case 'COMPLETED_TRIP_TIME':
      return {
        ...getTitleFromPanelType('COMPLETED_TRIP_TIME', formatMessage),
        formatValue: (value) => formatSeconds(value),
      };
    case 'DWELL_TIME':
      return {
        ...getTitleFromPanelType('DWELL_TIME', formatMessage),
        formatValue: (value) => formatSeconds(value),
      };
    default:
      errorReport.handled(
        new Error(`Failed to parse unsupported insight type: ${insightType}`)
      );
      return {
        title: insightType,
        formatValue: (value) => value.toString(),
      };
  }
}

export function getChartConfigFromChartType(
  insightType: InsightType,
  siteData: Partial<
    Pick<SiteInfo, 'siteUnit' | 'delayKPI' | 'queueKPI' | 'speedKPI'>
  >,
  formatMessage
): InsightChartConfig {
  const insightConfig = getInsightConfigFromType(
    insightType,
    siteData,
    formatMessage
  );
  switch (insightType) {
    case 'TIME_DELAY':
      return {
        ...insightConfig,
        mainAxis: {
          type: 'line',
          softMin: 0,
          formatValue: insightConfig.formatValue,
          title: formatMessage({
            defaultMessage: 'Delay (in minutes)',
            id: 'AWKvAr',
            description: 'Time delay yaxis label',
          }),
          interval: 60,
        },
        zoomingType: 'x',
      };
    case 'BUS_DELAY':
    case 'BUS_ACQUIRED_DELAY':
      return {
        title: insightConfig.title,
        kpi: insightConfig.kpi,
        marker: {
          enabled: true,
          symbol: 'circle',
          radius: 2,
        },
        mainAxis: {
          type: 'line',
          softMin: 0,
          title: formatMessage({
            defaultMessage: 'Trip delay time (in minutes)',
            id: '0VxeZk',
          }),
          interval: 60,
          formatValue: insightConfig.formatValue,
        },
        zoomingType: 'x',
      };
    case 'DUST':
      return {
        ...insightConfig,
        mainAxis: {
          type: 'line',
          softMin: 0,
          formatValue: insightConfig.formatValue,
          title: formatMessage({
            defaultMessage: 'Dust (in ug/m3)',
            id: 'e9eMA6',
            description: 'Environment yaxis label',
          }),
        },
        zoomingType: 'x',
      };
    case 'JOURNEY_TIME':
      return {
        ...insightConfig,
        mainAxis: {
          type: 'line',
          softMin: 0,
          formatValue: insightConfig.formatValue,
          title: formatMessage({
            defaultMessage: 'Travel time (in minutes)',
            id: '5bejev',
            description: 'Travel time yaxis label',
          }),
          interval: 60,
        },
        zoomingType: 'x',
      };
    case 'BUS_JOURNEY_TIME':
      return {
        title: insightConfig.title,
        kpi: insightConfig.kpi,
        disableTooltip: false,
        mainAxis: {
          type: 'scatter',
          softMin: 0,
          title: formatMessage({
            defaultMessage: 'Trip time (in minutes)',
            id: 'UOqYl6',
          }),
          interval: 60,
          formatValue: insightConfig.formatValue,
        },
        zoomingType: 'xy',
      };
    case 'NOISE':
      return {
        ...insightConfig,
        mainAxis: {
          type: 'line',
          softMin: 0,
          formatValue: insightConfig.formatValue,
          title: formatMessage({
            defaultMessage: 'Noise (in dB)',
            id: 'd9GwKQ',
            description: 'Noise yaxis label',
          }),
        },
        zoomingType: 'x',
      };
    case 'QUEUE_LENGTH':
      return {
        ...insightConfig,
        mainAxis: {
          type: 'column',
          softMin: 0,
          formatValue: insightConfig.formatValue,
          title: formatMessage(
            {
              defaultMessage: 'Queue length (in {queueLengthUnit})',
              id: 'W0ypkq',
              description: 'Queue yaxis label',
            },
            {
              queueLengthUnit:
                siteData.siteUnit === 'IMPERIAL' ? 'miles' : 'meters',
            }
          ),
        },
        zoomingType: 'x',
      };
    case 'RAIN':
      return {
        ...insightConfig,
        mainAxis: {
          type: 'line',
          softMin: 0,
          formatValue: insightConfig.formatValue,
          title: formatMessage({
            defaultMessage: 'Rain (in mm)',
            id: 'G3gDKu',
            description: 'Rain yaxis label',
          }),
        },
        zoomingType: 'x',
      };
    case 'BUS_SITE_IMPACT':
    case 'SITE_IMPACT_DELAY':
      return {
        ...insightConfig,
        mainAxis: {
          type: 'line',
          softMin: 0,
          formatValue: insightConfig.formatValue,
          title: formatMessage({
            defaultMessage: 'Delay time caused by site (in minutes)',
            id: '3rS9sV',
            description: 'Site impact yaxis label',
          }),
          interval: 60,
        },
        zoomingType: 'x',
      };
    case 'SPEED':
      return {
        ...insightConfig,
        mainAxis: {
          type: 'line',
          softMin: 0,
          formatValue: insightConfig.formatValue,
          title: formatMessage(
            {
              defaultMessage: 'Average Speed (in {speedUnit})',
              id: 'B4x8nM',
              description: 'Speed yaxis label',
            },
            {
              speedUnit: siteData.siteUnit === 'IMPERIAL' ? 'mph' : 'kmph',
            }
          ),
        },
        zoomingType: 'x',
      };
    case 'VEHICLE_STATION_COUNT':
      return {
        ...insightConfig,
        mainAxis: {
          type: 'column',
          softMin: 0,
          formatValue: insightConfig.formatValue,
          title: formatMessage({
            defaultMessage: 'Vehicle Count',
            id: 'FMAO6Z',
            description: 'Vehicle count yaxis label',
          }),
        },

        zoomingType: 'x',
      };
    case 'COMPLETED_TRIP_TIME':
      return {
        title: insightConfig.title,
        kpi: insightConfig.kpi,
        disableTooltip: true,
        mainAxis: {
          type: 'scatter',
          softMin: 0,
          title: formatMessage({
            defaultMessage: 'Trip time (in minutes)',
            id: 'UOqYl6',
          }),
          interval: 60,
          formatValue: insightConfig.formatValue,
        },
        zoomingType: 'xy',
      };
    case 'DWELL_TIME':
      return {
        ...insightConfig,
        mainAxis: {
          type: 'scatter',
          softMin: 0,
          formatValue: insightConfig.formatValue,
          title: formatMessage({
            defaultMessage: 'Dwell time (in minutes)',
            id: 'Bqyap+',
            description: 'Dwell time yaxis label',
          }),
          interval: 60,
        },
        zoomingType: 'xy',
      };
    default:
      errorReport.handled(
        new Error(`Failed to parse unsupported chart type: ${insightType}`)
      );
      return {
        ...insightConfig,
        mainAxis: {
          type: 'line',
          softMin: 0,
          formatValue: insightConfig.formatValue,
          title: formatMessage({
            defaultMessage: 'Unknown data type',
            id: 'yzwtpG',
            description: 'Unknown chart yaxis label',
          }),
        },
      };
  }
}

export function getInsightPerformanceConfigFromType(
  insightType: InsightType | LegacyInsightType,
  siteData: Partial<
    Pick<SiteInfo, 'siteUnit' | 'delayKPI' | 'queueKPI' | 'speedKPI'>
  >,
  formatMessage
): InsightPerformanceConfig {
  const insightConfig = getInsightConfigFromType(
    insightType,
    siteData,
    formatMessage
  );
  switch (insightType) {
    case 'TIME_DELAY':
    case 'DUST':
    case 'NOISE':
    case 'QUEUE_LENGTH':
    case 'RAIN':
    case 'SITE_IMPACT_DELAY':
    case 'SPEED':
    case 'JOURNEY_TIME':
      return {
        ...insightConfig,
        formatScoreValue: (value: number) => getAnomalyScorePercentage(value),
        colorForScoreValue: (value: number) => getAnomalyScoreColor(value),
      };
    default:
      errorReport.handled(
        new Error(`Failed to parse unsupported insight type: ${insightType}`)
      );
      return {
        ...insightConfig,
      };
  }
}

export function formatSeconds(timeInSecs) {
  if (!timeInSecs && timeInSecs !== 0) {
    return '';
  }
  const hour =
    timeInSecs > 0
      ? Math.floor(timeInSecs / 3600)
      : Math.ceil(timeInSecs / 3600);
  const min =
    timeInSecs > 0
      ? Math.floor(timeInSecs / 60) % 60
      : Math.ceil(timeInSecs / 60) % 60;
  const sec = Math.floor(timeInSecs) % 60;

  const rst =
    (hour !== 0 ? `${hour}h ` : '') +
    (min !== 0 ? `${min}m ` : '') +
    (sec !== 0 ? `${sec}s` : '');
  return rst === '' ? '0s' : rst;
}

// Highcharts Control Mode actions
const knobDistance = 24;

export const findPlotLineOrBandById = (id, xAxis) =>
  xAxis.plotLinesAndBands.find((p) => p.id === id);

const addPlotLine = (id, xAxis, xPos, color) =>
  xAxis.addPlotLine({
    value: xPos,
    width: 1,
    color,
    id,
    dashStyle: 'ShortDash',
    zIndex: 2,
  });

const addPlotLineByPixel = (id, xAxis, xPixel, color, chart) => {
  const plotLineValue = xAxis.toValue(xPixel - chart.plotLeft, true);
  addPlotLine(id, xAxis, plotLineValue, color);
};

const addPlotBandByPixels = (id, fromPixel, toPixel, xAxis, color, chart) => {
  const controlPlotBandStart = xAxis.toValue(fromPixel - chart.plotLeft, true);
  const controlPlotBandEnd = xAxis.toValue(toPixel - chart.plotLeft, true);
  return xAxis.addPlotBand({
    from: controlPlotBandStart,
    to: controlPlotBandEnd,
    color,
    id,
  });
};

const updateControlPlotBand = (id, from, to, chart) => {
  chart.xAxis[0].removePlotBand(id);
  chart.xAxis[0].addPlotBand({
    id: 'controlPlotBand',
    from,
    to,
    color: '#B9F6CA',
  });
};

const performUpdate = (
  fromValue,
  toValue,
  chart,
  callBack: (controlPoint: ControlPoint) => void
) => {
  if (fromValue > toValue) {
    callBack({
      start: {
        time: toValue,
        xPos: chart.xAxis[0].toPixels(toValue, false),
      },
      end: {
        time: fromValue,
        xPos: chart.xAxis[0].toPixels(fromValue, false),
      },
    });
  } else {
    callBack({
      start: {
        time: fromValue,
        xPos: chart.xAxis[0].toPixels(fromValue, false),
      },
      end: {
        time: toValue,
        xPos: chart.xAxis[0].toPixels(toValue, false),
      },
    });
  }
  updateControlPlotBand('controlPlotBand', fromValue, toValue, chart);
};

const chartMouseMove =
  (
    chart: InsightHighchartsType,
    callBack: (controlPoint: ControlPoint) => void
  ) =>
  (e) => {
    if (chart.mainKnob && chart.mainKnob.isDragging) {
      if (
        e.chartX >= chart.plotLeft &&
        e.chartX <= chart.plotLeft + chart.plotWidth
      ) {
        chart.mainKnob.attr({
          x: e.chartX - 16,
        });

        chart.xAxis[0].removePlotBand('mainPlotLine');
        const newXValue = chart.xAxis[0].toValue(
          e.chartX - chart.plotLeft,
          true
        );
        callBack({
          start: {
            time: newXValue,
            xPos: chart.xAxis[0].toPixels(newXValue, false),
          },
        });
        addPlotLine('mainPlotLine', chart.xAxis[0], newXValue, '#007C49');
        if (chart.leftPlus) {
          chart.leftPlus.attr({
            x: e.chartX - (knobDistance + 24),
          });
        }
        if (chart.rightPlus) {
          chart.rightPlus.attr({
            x: e.chartX + knobDistance,
          });
        }
      }
    }
    if (chart.mainKnobA && chart.mainKnobA.isDragging) {
      if (
        e.chartX >= chart.plotLeft &&
        e.chartX <= chart.plotLeft + chart.plotWidth
      ) {
        const controlPlotBand = findPlotLineOrBandById(
          'controlPlotBand',
          chart.xAxis[0]
        );
        if (controlPlotBand) {
          const newFromValue = chart.xAxis[0].toValue(
            e.chartX - chart.plotLeft,
            true
          );
          const originalToValue = controlPlotBand.options.to;
          performUpdate(newFromValue, originalToValue, chart, callBack);
          chart.xAxis[0].removePlotBand('plotLineA');
          addPlotLine('plotLineA', chart.xAxis[0], newFromValue, '#007C49');
          chart.mainKnobA.attr({
            x: e.chartX - 16,
          });
        }
      }
    }

    if (chart.mainKnobB && chart.mainKnobB.isDragging) {
      if (
        e.chartX >= chart.plotLeft &&
        e.chartX <= chart.plotLeft + chart.plotWidth
      ) {
        const controlPlotBand = findPlotLineOrBandById(
          'controlPlotBand',
          chart.xAxis[0]
        );
        if (controlPlotBand) {
          const originalFromValue = controlPlotBand.options.from;
          const newToValue = chart.xAxis[0].toValue(
            e.chartX - chart.plotLeft,
            true
          );
          performUpdate(originalFromValue, newToValue, chart, callBack);
          chart.xAxis[0].removePlotBand('plotLineB');
          addPlotLine('plotLineB', chart.xAxis[0], newToValue, '#007C49');
          chart.mainKnobB.attr({
            x: e.chartX - 16,
          });
        }
      }
    }
  };

const chartMouseUp = (chart: InsightHighchartsType) => () => {
  if (chart.mainKnob) {
    chart.mainKnob.isDragging = false;
  }
  if (chart.mainKnobA) {
    chart.mainKnobA.isDragging = false;
  }
  if (chart.mainKnobB) {
    chart.mainKnobB.isDragging = false;
  }
};

const addControlBandKnobs = (
  chart,
  leftKnobPixelX,
  rightKnobPixelX,
  pixelY
) => {
  chart.mainKnobA = chart.renderer
    .image(imagePathControlKnob, leftKnobPixelX - 16, pixelY, 32, 32)
    .on('mousedown', () => {
      if (chart.mainKnobA) {
        chart.mainKnobA.isDragging = true;
      }
    })
    .on('touchstart', () => {
      if (chart.mainKnobA) {
        chart.mainKnobA.isDragging = true;
      }
    })
    .attr({ zIndex: 7, cursor: 'pointer' })
    .add();
  chart.mainKnobB = chart.renderer
    .image(imagePathControlKnob, rightKnobPixelX - 16, pixelY, 32, 32)
    .on('mousedown', () => {
      if (chart.mainKnobB) {
        chart.mainKnobB.isDragging = true;
      }
    })
    .on('touchstart', () => {
      if (chart.mainKnobB) {
        chart.mainKnobB.isDragging = true;
      }
    })
    .attr({ zIndex: 7, cursor: 'pointer' })
    .add();
};

const updateControlKnob = (knob, plotLine, xAxis) => {
  const xValeOnAxis = plotLine.options.value;
  const mainKnobBPixelX = xAxis.toPixels(xValeOnAxis, false);
  knob.attr({ x: mainKnobBPixelX - 16 });
};

const destroyFirstPhaseControl = (chart: InsightHighchartsType) => {
  if (chart) {
    if (chart.mainKnob) {
      chart.xAxis[0].removePlotBand('mainPlotLine');
      chart.mainKnob.destroy();
      chart.mainKnob = undefined;
    }
    if (chart.leftPlus) {
      chart.leftPlus.destroy();
      chart.leftPlus = undefined;
    }
    if (chart.rightPlus) {
      chart.rightPlus.destroy();
      chart.rightPlus = undefined;
    }
  }
};

const destroySecondPhaseControl = (chart: InsightHighchartsType) => {
  if (chart) {
    if (chart.mainKnobA) {
      chart.mainKnobA.destroy();
      chart.mainKnobA = undefined;
    }
    if (chart.mainKnobB) {
      chart.mainKnobB.destroy();
      chart.mainKnobB = undefined;
    }
    chart.xAxis[0].removePlotBand('controlPlotBand');
    chart.xAxis[0].removePlotBand('plotLineA');
    chart.xAxis[0].removePlotBand('plotLineB');
  }
};

const createSecondPhaseKnobs = (
  leftKnobPixelX,
  rightKnobPixelX,
  mainPixelY,
  chart
) => {
  addPlotBandByPixels(
    'controlPlotBand',
    leftKnobPixelX,
    rightKnobPixelX,
    chart.xAxis[0],
    '#B9F6CA',
    chart
  );
  addControlBandKnobs(chart, leftKnobPixelX, rightKnobPixelX, mainPixelY);
  addPlotLineByPixel(
    'plotLineA',
    chart.xAxis[0],
    leftKnobPixelX,
    '#007C49',
    chart
  );
  addPlotLineByPixel(
    'plotLineB',
    chart.xAxis[0],
    rightKnobPixelX,
    '#007C49',
    chart
  );
};

export type InsightHighchartsType = Highcharts.Chart & {
  touchMove?: (e) => void;
  touchEnd?: (e) => void;
  touchCancel?: (e) => void;
  mouseMove?: (e) => void;
  mouseUp?: (e) => void;
  mouseLeave?: (e) => void;
  mainKnob?: Highcharts.SVGElement & { isDragging?: boolean };
  leftPlus?: Highcharts.SVGElement;
  rightPlus?: Highcharts.SVGElement;
  mainKnobA?: Highcharts.SVGElement & { isDragging?: boolean };
  mainKnobB?: Highcharts.SVGElement & { isDragging?: boolean };
  popover?: React.RefObject<HTMLDivElement | null>;
  xPosDiff?: number;
};

type ChartPoint = {
  time: number;
  xPos: number;
};

export type ControlPoint = {
  start: ChartPoint;
  end?: ChartPoint;
};

export const goToSecondPhaseOnTheRight = (
  updateControlStatus: (controlPoint: ControlPoint) => void,
  chart?: InsightHighchartsType
) => {
  if (chart) {
    if (chart.rightPlus) {
      const mainPlotLine = findPlotLineOrBandById(
        'mainPlotLine',
        chart.xAxis[0]
      );
      if (mainPlotLine) {
        const xValeOnAxis = mainPlotLine.options.value;
        const leftKnobPixelX = chart.xAxis[0].toPixels(xValeOnAxis, false);
        const rightKnobPixelX = leftKnobPixelX + 32;
        const rightKnobValueX = chart.xAxis[0].toValue(
          rightKnobPixelX - chart.plotLeft,
          true
        );
        updateControlStatus({
          start: { time: xValeOnAxis, xPos: leftKnobPixelX },
          end: { time: rightKnobValueX, xPos: rightKnobPixelX },
        });
        createSecondPhaseKnobs(
          leftKnobPixelX,
          rightKnobPixelX,
          chart.yAxis[0].toPixels(chart.yAxis[0].max!, false),
          chart
        );
      }
      destroyFirstPhaseControl(chart);
    }
  }
};

const createFirstPhaseControl = (
  mainPointX,
  chart: InsightHighchartsType,
  updateControlStatus: (controlPoint: ControlPoint) => void
) => {
  destroySecondPhaseControl(chart);
  const { renderer } = chart;
  const mainPixelX = chart.xAxis[0].toPixels(mainPointX, false);
  const mainPixelY = chart.yAxis[0].toPixels(chart.yAxis[0].max!, false);
  addPlotLine('mainPlotLine', chart.xAxis[0], mainPointX, '#007C49');
  updateControlStatus({
    start: { time: mainPointX, xPos: mainPixelX },
  });
  chart.mainKnob = renderer
    .image(imagePathControlKnob, mainPixelX - 16, mainPixelY, 32, 32)
    .on('mousedown', () => {
      if (chart.mainKnob) {
        chart.mainKnob.isDragging = true;
      }
    })
    .on('touchstart', () => {
      if (chart.mainKnob) {
        chart.mainKnob.isDragging = true;
      }
    })
    .attr({ zIndex: 7, cursor: 'pointer' })
    .add();
  chart.leftPlus = renderer
    .image(
      imagePathControlPlus,
      mainPixelX - (knobDistance + 24),
      mainPixelY + 4,
      24,
      24
    )
    .on('mousedown', () => {
      if (chart.leftPlus) {
        const mainPlotLine = findPlotLineOrBandById(
          'mainPlotLine',
          chart.xAxis[0]
        );
        if (mainPlotLine) {
          const xValeOnAxis = mainPlotLine.options.value;
          const rightKnobPixelX = chart.xAxis[0].toPixels(xValeOnAxis, false);
          const leftKnobPixelX = rightKnobPixelX - 32;
          const leftKnobValueX = chart.xAxis[0].toValue(
            leftKnobPixelX - chart.plotLeft,
            true
          );
          updateControlStatus({
            start: { time: leftKnobValueX, xPos: leftKnobPixelX },
            end: { time: xValeOnAxis, xPos: rightKnobPixelX },
          });
          createSecondPhaseKnobs(
            leftKnobPixelX,
            rightKnobPixelX,
            mainPixelY,
            chart
          );
        }
        destroyFirstPhaseControl(chart);
      }
    })
    .attr({ zIndex: 7, cursor: 'pointer' })
    .add();
  chart.rightPlus = renderer
    .image(
      imagePathControlPlus,
      mainPixelX + knobDistance,
      mainPixelY + 4,
      24,
      24
    )
    .on('mousedown', () => {
      goToSecondPhaseOnTheRight(updateControlStatus, chart);
    })
    .attr({ zIndex: 7, cursor: 'pointer' })
    .add();
};

export const backToFirstPhaseControl = (
  updateControlStatus: (controlPoint: ControlPoint) => void,
  chart?: InsightHighchartsType
) => {
  if (chart) {
    const plotLineA = findPlotLineOrBandById('plotLineA', chart.xAxis[0]);
    const plotLineB = findPlotLineOrBandById('plotLineB', chart.xAxis[0]);
    if (plotLineA && plotLineB) {
      let valueX;
      if (plotLineA.options.value < plotLineB.options.value) {
        valueX = plotLineA.options.value;
      } else {
        valueX = plotLineB.options.value;
      }
      createFirstPhaseControl(valueX, chart, updateControlStatus);
    }
  }
};

export const updateChartWhenStartDateChange = (
  updateControlStatus: (controlPoint: ControlPoint) => void,
  startDateEpoch: number | undefined,
  chart: InsightHighchartsType | undefined
) => {
  if (chart) {
    if (chart.mainKnob && startDateEpoch) {
      const mainKnobPixelX = chart.xAxis[0].toPixels(
        startDateEpoch * 1000,
        false
      );
      chart.xAxis[0].removePlotBand('mainPlotLine');
      addPlotLine(
        'mainPlotLine',
        chart.xAxis[0],
        startDateEpoch * 1000,
        '#007C49'
      );
      chart.mainKnob.attr({ x: mainKnobPixelX - 16 });
      if (chart.leftPlus) {
        chart.leftPlus.attr({
          x: mainKnobPixelX - (knobDistance + 24),
        });
      }
      if (chart.rightPlus) {
        chart.rightPlus.attr({
          x: mainKnobPixelX + knobDistance,
        });
      }
      updateControlStatus({
        start: { time: startDateEpoch * 1000, xPos: mainKnobPixelX },
      });
    }

    if (chart.mainKnobA && chart.mainKnobB && startDateEpoch) {
      const plotLineA = findPlotLineOrBandById('plotLineA', chart.xAxis[0]);
      const plotLineB = findPlotLineOrBandById('plotLineB', chart.xAxis[0]);
      if (plotLineA && plotLineB) {
        const controlPlotBand = findPlotLineOrBandById(
          'controlPlotBand',
          chart.xAxis[0]
        );
        const knobPixelX = chart.xAxis[0].toPixels(
          startDateEpoch * 1000,
          false
        );
        if (plotLineA.options.value < plotLineB.options.value) {
          const originalToValue = controlPlotBand.options.to;
          chart.xAxis[0].removePlotBand('plotLineA');
          addPlotLine(
            'plotLineA',
            chart.xAxis[0],
            startDateEpoch * 1000,
            '#007C49'
          );
          chart.mainKnobA.attr({
            x: knobPixelX - 16,
          });
          performUpdate(
            startDateEpoch * 1000,
            originalToValue,
            chart,
            updateControlStatus
          );
        } else {
          const originalFromValue = controlPlotBand.options.from;
          chart.xAxis[0].removePlotBand('plotLineB');
          addPlotLine(
            'plotLineB',
            chart.xAxis[0],
            startDateEpoch * 1000,
            '#007C49'
          );
          chart.mainKnobB.attr({
            x: knobPixelX - 16,
          });
          performUpdate(
            originalFromValue,
            startDateEpoch * 1000,
            chart,
            updateControlStatus
          );
        }
      }
    }
  }
};

export const updateChartWhenEndDateChange = (
  updateControlStatus: (controlPoint: ControlPoint) => void,
  endDateEpoch: number | undefined,
  chart: InsightHighchartsType | undefined
) => {
  if (chart) {
    if (chart.mainKnobA && chart.mainKnobB && endDateEpoch) {
      const controlPlotBand = findPlotLineOrBandById(
        'controlPlotBand',
        chart.xAxis[0]
      );
      const plotLineA = findPlotLineOrBandById('plotLineA', chart.xAxis[0]);
      const plotLineB = findPlotLineOrBandById('plotLineB', chart.xAxis[0]);
      if (plotLineA && plotLineB) {
        const knobPixelX = chart.xAxis[0].toPixels(endDateEpoch * 1000, false);
        if (plotLineA.options.value < plotLineB.options.value) {
          chart.xAxis[0].removePlotBand('plotLineB');
          addPlotLine(
            'plotLineB',
            chart.xAxis[0],
            endDateEpoch * 1000,
            '#007C49'
          );
          chart.mainKnobB.attr({
            x: knobPixelX - 16,
          });
          performUpdate(
            controlPlotBand.options.from,
            endDateEpoch * 1000,
            chart,
            updateControlStatus
          );
        } else {
          chart.xAxis[0].removePlotBand('plotLineA');
          addPlotLine(
            'plotLineA',
            chart.xAxis[0],
            endDateEpoch * 1000,
            '#007C49'
          );
          chart.mainKnobA.attr({
            x: knobPixelX - 16,
          });
          performUpdate(
            endDateEpoch * 1000,
            controlPlotBand.options.to,
            chart,
            updateControlStatus
          );
        }
      }
    }
  }
};

export type UpdateChartCallbacks = {
  backToFirstPhase: () => void;
  goToSecondPhase: () => void;
  updateChartStartDate: (startDateEpoch: number | undefined) => void;
  updateChartEndDate: (endDateEpoch: number | undefined) => void;
};

export const startControlMode = (
  chart: InsightHighchartsType | undefined,
  updateControlStatus: (controlPoint: ControlPoint) => void
) => {
  if (chart && chart.xAxis) {
    const xAxis = chart.xAxis[0];
    const mainCenterPointX = (xAxis.min! + xAxis.max!) / 2;
    createFirstPhaseControl(mainCenterPointX, chart, updateControlStatus);
    chart.touchMove = chartMouseMove(chart, updateControlStatus);
    chart.container.addEventListener('touchmove', chart.touchMove);
    chart.mouseMove = chartMouseMove(chart, updateControlStatus);
    chart.container.addEventListener('mousemove', chart.mouseMove);
    chart.touchEnd = chartMouseUp(chart);
    chart.container.addEventListener('touchend', chart.touchEnd);
    chart.mouseUp = chartMouseUp(chart);
    chart.container.addEventListener('mouseup', chart.mouseUp);
    chart.mouseLeave = chartMouseUp(chart);
    chart.container.addEventListener('mouseleave', chart.mouseLeave);
  }
};

export const cancelControlMode = (chart: InsightHighchartsType | undefined) => {
  if (chart) {
    if (chart.touchMove) {
      chart.container.removeEventListener('touchmove', chart.touchMove);
    }
    if (chart.mouseMove) {
      chart.container.removeEventListener('mousemove', chart.mouseMove);
    }
    if (chart.touchEnd) {
      chart.container.removeEventListener('touchend', chart.touchEnd);
    }
    if (chart.mouseUp) {
      chart.container.removeEventListener('mouseup', chart.mouseUp);
    }
    if (chart.mouseLeave) {
      chart.container.removeEventListener('mouseleave', chart.mouseLeave);
    }
    destroyFirstPhaseControl(chart);
    destroySecondPhaseControl(chart);
  }
};

export const positionPopover = (
  xPos: number,
  chart?: InsightHighchartsType
): number => {
  if (chart && chart.popover?.current) {
    const popoverWidth = chart.popover.current.clientWidth;
    const halfOfContentWidth = popoverWidth / 2;
    const maxLeft = chart.chartWidth - popoverWidth;
    let currentLeft = xPos - halfOfContentWidth;
    if (currentLeft < 0) {
      currentLeft = 0;
    }
    if (currentLeft > maxLeft) {
      currentLeft = maxLeft;
    }
    let xPosDiff = xPos - currentLeft - halfOfContentWidth;
    if (Number.isNaN(xPosDiff)) {
      xPosDiff = 0;
    }
    chart.xPosDiff = xPosDiff;
    return currentLeft;
  }
  return 0;
};

export const afterChartRender = (chart: InsightHighchartsType) => {
  const xAxis = chart.xAxis[0];
  const mainPlotLine = findPlotLineOrBandById('mainPlotLine', xAxis);
  if (mainPlotLine && chart.mainKnob) {
    const xValeOnAxis = mainPlotLine.options.value;
    const mainKnobPixelX = xAxis.toPixels(xValeOnAxis, false);
    if (chart.popover && chart.popover.current) {
      chart.popover.current.style.left = `${positionPopover(
        mainKnobPixelX,
        chart
      )}px`;
    }
    chart.mainKnob.attr({ x: mainKnobPixelX - 16 });
    if (chart.leftPlus) {
      chart.leftPlus.attr({
        x: mainKnobPixelX - (knobDistance + 24),
      });
    }
    if (chart.rightPlus) {
      chart.rightPlus.attr({
        x: mainKnobPixelX + knobDistance,
      });
    }
  }
  const plotLineA = findPlotLineOrBandById('plotLineA', xAxis);
  if (plotLineA && chart.mainKnobA) {
    updateControlKnob(chart.mainKnobA, plotLineA, xAxis);
  }

  const plotLineB = findPlotLineOrBandById('plotLineB', xAxis);
  if (plotLineB && chart.mainKnobB) {
    updateControlKnob(chart.mainKnobB, plotLineB, xAxis);
  }

  if (plotLineA && plotLineB) {
    let leftValue;
    if (plotLineA.options.value < plotLineB.options.value) {
      leftValue = xAxis.toPixels(plotLineA.options.value, false);
    } else {
      leftValue = xAxis.toPixels(plotLineB.options.value, false);
    }
    if (chart.popover && chart.popover.current) {
      chart.popover.current.style.left = `${positionPopover(
        leftValue,
        chart
      )}px`;
    }
  }
};
